import { useCallback, useRef, useState } from "react";
import { SignaturePad, SignaturePadHandle } from "@/components/SignaturePad";
import { ZapIcon } from "@/components/icons";
import type { ModalConfig } from "@/components/Modal";

/* Contract step. The contract body is the REAL server-rendered template
   (leoparda picks Comfort vs CPX from the frozen terms) shown in a sandboxed
   iframe — the LP never rebuilds a summary that could drift from the legal
   text. Signing mirrors the ops-app experience: review → explicit consent →
   canvas signature → submit. */

export function ContractScreen({
  contractHtml,
  termMonths,
  alreadySigned,
  maskedEmail,
  busy,
  errorReason,
  openModal,
  onSign,
  onContinueSigned,
}: {
  contractHtml: string;
  // The term the rider signs (36 − tenure credit), same as the plan card and
  // the confirm modal. The consent modal must not promise a longer contract.
  termMonths: number;
  alreadySigned: boolean;
  maskedEmail: string | null;
  busy: boolean;
  errorReason: string | null;
  openModal: (config: ModalConfig) => void;
  onSign: (signatureDataUrl: string) => void;
  onContinueSigned: () => void;
}) {
  const [signing, setSigning] = useState(false);
  const [inked, setInked] = useState(false);
  const padHandle = useRef<SignaturePadHandle | null>(null);
  const registerHandle = useCallback((h: SignaturePadHandle) => {
    padHandle.current = h;
  }, []);

  return (
    <section className="screen-enter" data-testid="screen-contract">
      <h2>{alreadySigned ? "Contrato assinado" : "Falta só assinar"}</h2>

      <div className="lpc-contract" data-testid="contract-frame">
        <iframe title="Contrato Vammo Conquista" sandbox="" srcDoc={contractHtml} />
      </div>

      {alreadySigned ? (
        <>
          <div className="lpc-note">
            <ZapIcon />
            <span>
              Seu contrato já está assinado — a cópia foi pro seu email. Falta só concluir.
            </span>
          </div>
          <button
            className="btn btn--primary"
            data-testid="continue-signed"
            disabled={busy}
            onClick={onContinueSigned}
          >
            {busy ? "Concluindo…" : "Concluir migração"}
          </button>
          {errorReason ? (
            <div className="lpc-warn" data-testid="confirm-error">
              <ZapIcon />
              <span>
                Não conseguimos concluir agora ({errorReason}). Tenta de novo — seu pagamento e sua
                assinatura estão guardados.
              </span>
            </div>
          ) : null}
        </>
      ) : !signing ? (
        <>
          <div className="lpc-note">
            <ZapIcon />
            <span>
              Depois dessa assinatura, cada semana rodada te deixa mais perto de uma moto no seu
              nome.
            </span>
          </div>
          <button
            className="btn btn--primary"
            data-testid="open-sign"
            onClick={() =>
              openModal({
                title: "Essa mudança é definitiva",
                body: `Você está migrando pro Vammo Conquista por ${termMonths} meses.`,
                danger: "Ao assinar, não dá pra voltar pro plano atual depois.",
                confirmLabel: "Entendi, quero assinar",
                onConfirm: () => setSigning(true),
              })
            }
          >
            Revisar e assinar contrato
          </button>
        </>
      ) : (
        <>
          <SignaturePad onInkChange={setInked} registerHandle={registerHandle} />
          <div className="lpc-signactions">
            <button
              className="btn--link"
              data-testid="clear-signature"
              onClick={() => padHandle.current?.clear()}
            >
              Limpar e assinar de novo
            </button>
          </div>
          <button
            className="btn btn--primary"
            data-testid="submit-signature"
            disabled={!inked || busy}
            onClick={() => {
              const dataUrl = padHandle.current?.toDataUrl();
              if (dataUrl) onSign(dataUrl);
            }}
          >
            {busy ? "Enviando…" : "Assinar contrato"}
          </button>
          {errorReason ? (
            <div className="lpc-warn" data-testid="sign-error">
              <ZapIcon />
              <span>Não conseguimos registrar sua assinatura ({errorReason}). Tenta de novo.</span>
            </div>
          ) : null}
        </>
      )}

      <p className="lpc-micro">
        Depois de assinado, você recebe uma cópia do contrato no seu email
        {maskedEmail ? <span data-testid="masked-email"> {maskedEmail}</span> : ""}.
      </p>
    </section>
  );
}
